/**
 * TanStack Query hooks — Intake Links (Chunks 9.N.3.A + 9.N.3.B — broker UI).
 *
 * Backend :
 *   - apps/api/src/routes/intake-links.ts (chunks 9.N.1.A + 9.N.3.A.0 + 9.N.3.B.0)
 *       GET    /v1/intake-links?status&project_id&cursor&limit
 *       GET    /v1/intake-links/:id                        (9.N.3.B.0)
 *       POST   /v1/intake-links                            → bundle UNE FOIS
 *       PATCH  /v1/intake-links/:id/revoke
 *   - apps/api/src/routes/intake-events.ts (chunk 9.N.3.B.0)
 *       GET    /v1/intake-events?intake_link_id=...&cursor&limit
 *   - apps/api/src/routes/document-links.ts (chunk 9.F)
 *       GET    /v1/document-links?attachable_kind=intake_event&attachable_id=...
 *
 * Sécurité front :
 *   - Le bundle POST contient `intake_token` + `intake_url` UNE SEULE FOIS.
 *     Aucune requête GET ne les ré-expose (anti-leak vérifié 9.N.3.A.0 backend
 *     + 9.N.3.B page détail). Le composant TokenReveal les affiche
 *     immédiatement et ne les persiste PAS hors du `useState` local.
 *   - Anti-leak strict : pas de `console.log` du token/URL/storage_path/signed
 *     URL nulle part dans la chaîne queries → composants → handlers.
 *   - Les erreurs HTTP sont déjà bilingues (ApiError du wire). Le composant
 *     consomme `getApiErrorMessage(err, lang)` pour l'affichage utilisateur.
 *
 * Convention queryKey :
 *   ['intake-links', 'list', { cursor, limit, status, project_id }]
 *   ['intake-links', 'detail', id]                            (9.N.3.B)
 *   ['intake-events', 'by-link', { linkId, cursor, limit }]   (9.N.3.B)
 *   ['document-links', 'by-event', eventId]                   (9.N.3.B)
 *
 * Invalidation :
 *   - useCreateIntakeLink → invalidate ['intake-links']
 *   - useRevokeIntakeLink → invalidate ['intake-links'] (couvre liste + detail)
 */

import type {
  CreateIntakeLinkInput,
  DocumentLinkRow,
  IntakeEventBrokerSanitizedRow,
  IntakeLinkBundle,
  IntakeLinkListItem,
  IntakeLinkStatusFilter,
  RejectIntakeEventInput,
  ResolveIntakeEventInput,
} from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";

// -----------------------------------------------------------------------------
// Types — re-export ergonomiques
// -----------------------------------------------------------------------------

export type IntakeLink = IntakeLinkListItem;
export type IntakeLinkBundleResult = IntakeLinkBundle;
export type IntakeLinkListResult = {
  items: IntakeLinkListItem[];
  next_cursor: string | null;
};

export type IntakeLinkListParams = {
  cursor?: string | null;
  limit?: number;
  /** Défaut backend = "all". `undefined` = pas de filtre côté requête. */
  status?: IntakeLinkStatusFilter;
  project_id?: string;
};

// -----------------------------------------------------------------------------
// GET /v1/intake-links — liste broker
// -----------------------------------------------------------------------------

function buildListPath(params: IntakeLinkListParams): string {
  const sp = new URLSearchParams();
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  // "all" est le default backend ; on ne le pousse que s'il diverge.
  if (params.status && params.status !== "all") sp.set("status", params.status);
  if (params.project_id) sp.set("project_id", params.project_id);
  const qs = sp.toString();
  return qs ? `/v1/intake-links?${qs}` : "/v1/intake-links";
}

export function useIntakeLinks(params: IntakeLinkListParams) {
  return useQuery({
    queryKey: ["intake-links", "list", params],
    queryFn: () => api.get<IntakeLinkListResult>(buildListPath(params)),
    placeholderData: (prev) => prev,
  });
}

/** Complete project-scoped view: an invitation must not disappear behind pagination. */
export function useProjectIntakeLinks(projectId: string) {
  return useQuery({
    queryKey: ["intake-links", "project-invitations", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const items: IntakeLink[] = [];
      const seen = new Set<string>();
      let cursor: string | null = null;
      do {
        const page: IntakeLinkListResult = await api.get(
          buildListPath({ project_id: projectId, limit: 100, cursor }),
        );
        items.push(...page.items);
        cursor = page.next_cursor;
        if (cursor && seen.has(cursor)) throw new Error("Repeated pagination cursor");
        if (cursor) seen.add(cursor);
      } while (cursor);
      return items;
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });
}

// -----------------------------------------------------------------------------
// POST /v1/intake-links — création (bundle token retourné UNE FOIS)
// -----------------------------------------------------------------------------

export function useCreateIntakeLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateIntakeLinkInput) =>
      api.post<IntakeLinkBundleResult>(
        "/v1/intake-links",
        input as unknown as Record<string, unknown>,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["intake-links"] });
    },
  });
}

// -----------------------------------------------------------------------------
// PATCH /v1/intake-links/:id/revoke — révocation soft idempotente
// -----------------------------------------------------------------------------

export function useRevokeIntakeLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.patch<IntakeLink>(`/v1/intake-links/${id}/revoke`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["intake-links"] });
    },
  });
}

// -----------------------------------------------------------------------------
// PATCH /v1/intake-events/:id/resolve  (Chunk 9.G.2 — frontend)
// PATCH /v1/intake-events/:id/reject   (Chunk 9.G.2 — frontend)
//
// Phase 1 UI : Q1=B project-only resolve. QuoteRequestPicker + flow
// `resolution_kind: "quote_request"` réservés Phase 2 (dette P3 ouverte).
// Le backend accepte les 2 kinds depuis 9.G.1, le frontend pousse uniquement
// `{resolution_kind: "project", target_id: <project uuid>}` Phase 1.
//
// Invalidation Q10=B : par lien spécifique (counter pending de la detail page)
// + liste events du lien. Pas d'invalidation tenant-wide (la liste tenant-wide
// re-fetchera au prochain mount, pas user-facing critique Phase 1).
//
// Q5=B race 409 INVALID_TRANSITION : le composant appelant catch et close
// dialog gracefully + auto-refetch parent. Le hook ne fait rien de spécial.
// -----------------------------------------------------------------------------

type IntakeEventMutationVars = {
  /** UUID intake_event ciblé. */
  readonly eventId: string;
  /**
   * UUID intake_link parent — pour invalidation ciblée. `null` pour les
   * intake_events orphans (9.T.3 broker upload : `intake_link_id=NULL`).
   * Quand null, on invalide aussi la query detail event au lieu de la query
   * detail link.
   */
  readonly linkId: string | null;
};

type ResolveMutationVars = IntakeEventMutationVars & {
  readonly input: ResolveIntakeEventInput;
};

type RejectMutationVars = IntakeEventMutationVars & {
  readonly input: RejectIntakeEventInput;
};

export function useResolveIntakeEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, input }: ResolveMutationVars) =>
      api.patch<IntakeEventBrokerSanitizedRow>(
        `/v1/intake-events/${eventId}/resolve`,
        input as unknown as Record<string, unknown>,
      ),
    onSuccess: (_data, { linkId, eventId }) => {
      void qc.invalidateQueries({ queryKey: ["intake-events", "by-link"] });
      void qc.invalidateQueries({ queryKey: ["intake-events", "list"] });
      if (linkId) {
        void qc.invalidateQueries({ queryKey: ["intake-links", "detail", linkId] });
      }
      void qc.invalidateQueries({ queryKey: ["intake-events", "detail", eventId] });
    },
  });
}

export function useRejectIntakeEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, input }: RejectMutationVars) =>
      api.patch<IntakeEventBrokerSanitizedRow>(
        `/v1/intake-events/${eventId}/reject`,
        input as unknown as Record<string, unknown>,
      ),
    onSuccess: (_data, { linkId, eventId }) => {
      void qc.invalidateQueries({ queryKey: ["intake-events", "by-link"] });
      void qc.invalidateQueries({ queryKey: ["intake-events", "list"] });
      if (linkId) {
        void qc.invalidateQueries({ queryKey: ["intake-links", "detail", linkId] });
      }
      void qc.invalidateQueries({ queryKey: ["intake-events", "detail", eventId] });
    },
  });
}

// -----------------------------------------------------------------------------
// 9.N.3.B — Détail d'un lien + listing events + document links par event
// -----------------------------------------------------------------------------

/**
 * GET /v1/intake-links/:id — détail d'un lien (9.N.3.B.0).
 *
 * Réponse : `IntakeLinkListItem` (status dérivé + project enrichi).
 * Anti-leak token : la réponse ne contient JAMAIS `intake_token` ni `intake_url`.
 * 404 → ApiException(404, INTAKE_LINKS_NOT_FOUND) — le composant doit
 * différencier ce cas du « lien existe mais aucun event » (cf. IntakeLinkDetailPage).
 */
export function useIntakeLink(id: string | undefined) {
  return useQuery({
    queryKey: ["intake-links", "detail", id],
    queryFn: () => api.get<IntakeLink>(`/v1/intake-links/${id}`),
    enabled: !!id,
  });
}

/**
 * GET /v1/intake-events?intake_link_id=...&cursor=&limit= — submissions liées
 * à un lien d'intake (9.N.3.B.0).
 *
 * Cursor pagination (parité tenants/companies — keyset created_at desc, id desc).
 * Filtre `intake_link_id` UUID strict côté backend (Zod 400 sur non-UUID).
 */
/**
 * Status enum exposé côté UI broker (5 valeurs DB + sentinel "all" pour
 * l'absence de filter — gardé côté UI uniquement, jamais envoyé au backend).
 */
export type IntakeEventStatusFilter =
  | "all"
  | "pending_classification"
  | "pending_resolution"
  | "resolved"
  | "rejected"
  | "superseded";

export type IntakeEventsByLinkParams = {
  linkId: string | undefined;
  cursor?: string | null;
  limit?: number;
  /** 9.N.3.C — filter status (sentinel "all" = pas envoyé au backend) */
  status?: IntakeEventStatusFilter;
  /** 9.N.3.C — fenêtre `created_at` borne basse incluse (ISO datetime) */
  from?: string;
  /** 9.N.3.C — fenêtre `created_at` borne haute incluse (ISO datetime) */
  to?: string;
  /** 9.N.3.C — search ilike sur payload->>'comment' + payload->>'email'. Min 2 chars enforced backend. */
  q?: string;
};

export type IntakeEventListResult = {
  items: IntakeEventBrokerSanitizedRow[];
  next_cursor: string | null;
};

function buildEventsByLinkPath(params: IntakeEventsByLinkParams): string {
  const sp = new URLSearchParams();
  sp.set("intake_link_id", params.linkId!); // garde-fou enabled empêche undefined
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.status && params.status !== "all") sp.set("status", params.status);
  if (params.from) sp.set("from", params.from);
  if (params.to) sp.set("to", params.to);
  if (params.q && params.q.length >= 2) sp.set("q", params.q);
  return `/v1/intake-events?${sp.toString()}`;
}

/**
 * Construit le path CSV export (Chunk 9.N.3.C) — même shape de query que la
 * liste, sans cursor ni limit (le backend cap lui-même 5000 rows + retourne
 * `X-Truncated: true` si plus). Réutilisé par le bouton « Exporter CSV ».
 */
export function buildEventsByLinkCsvPath(params: IntakeEventsByLinkParams): string {
  const sp = new URLSearchParams();
  sp.set("intake_link_id", params.linkId!);
  if (params.status && params.status !== "all") sp.set("status", params.status);
  if (params.from) sp.set("from", params.from);
  if (params.to) sp.set("to", params.to);
  if (params.q && params.q.length >= 2) sp.set("q", params.q);
  return `/v1/intake-events/csv?${sp.toString()}`;
}

export function useIntakeEventsByLink(params: IntakeEventsByLinkParams) {
  return useQuery({
    queryKey: ["intake-events", "by-link", params],
    queryFn: () => api.get<IntakeEventListResult>(buildEventsByLinkPath(params)),
    enabled: !!params.linkId,
    placeholderData: (prev) => prev,
  });
}

// -----------------------------------------------------------------------------
// 9.P.1 — Hook intake-events global (sans linkId requis) pour le DashboardPage.
// Backend GET /v1/intake-events supporte déjà status/from/to/q sans intake_link_id.
// -----------------------------------------------------------------------------

export type IntakeEventsParams = {
  cursor?: string | null;
  limit?: number;
  status?: IntakeEventStatusFilter;
  /** ISO datetime — borne basse `created_at >= from` (incluse). */
  from?: string;
  /** ISO datetime — borne haute `created_at <= to` (incluse). */
  to?: string;
  /** Recherche backend ilike sur payload comment/email. Min 2 chars. */
  q?: string;
};

function buildEventsPath(params: IntakeEventsParams): string {
  const sp = new URLSearchParams();
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.status && params.status !== "all") sp.set("status", params.status);
  if (params.from) sp.set("from", params.from);
  if (params.to) sp.set("to", params.to);
  if (params.q && params.q.length >= 2) sp.set("q", params.q);
  const qs = sp.toString();
  return qs ? `/v1/intake-events?${qs}` : "/v1/intake-events";
}

/**
 * GET /v1/intake-events (tenant-wide, status filter).
 *
 * Consommé par le DashboardPage 9.P.1 pour afficher les soumissions à classer
 * (status `pending_classification` ou `pending_resolution`) tous liens
 * confondus. Pas de hook list-by-link required ici — c'est volontairement
 * une vue tenant-wide.
 */
export function useIntakeEvents(params: IntakeEventsParams) {
  return useQuery({
    queryKey: ["intake-events", "list", params],
    queryFn: () => api.get<IntakeEventListResult>(buildEventsPath(params)),
    placeholderData: (prev) => prev,
  });
}

/**
 * GET /v1/document-links?attachable_kind=intake_event&attachable_id=<eventId>
 * — fichiers attachés à un intake event (chunk 9.F + réutilisé 9.N.3.B).
 *
 * Activé seulement quand l'`IntakeEventCard` est expanded (lazy-load) — le
 * paramètre `enabled` est exposé pour permettre au caller de gater la query.
 * `staleTime: 30_000` ms : les fichiers ne bougent pas une fois la submission
 * persistée (immutable post-POST), donc on évite des refetch inutiles entre
 * collapse / expand.
 */
export type EventDocumentLinksResult = {
  items: DocumentLinkRow[];
  next_cursor: string | null;
};

export function useEventDocumentLinks(
  eventId: string | undefined,
  options?: { enabled?: boolean },
) {
  const enabledFlag = options?.enabled ?? true;
  return useQuery({
    queryKey: ["document-links", "by-event", eventId],
    queryFn: () =>
      api.get<EventDocumentLinksResult>(
        `/v1/document-links?attachable_kind=intake_event&attachable_id=${eventId}`,
      ),
    enabled: !!eventId && enabledFlag,
    staleTime: 30_000,
  });
}
